/**
 * Imports
 */
import _round from 'lodash/round';

/**
 * DateTime utility functions.
 */
class DateTimeUtils {
  /**
   * Converts the provided seconds into minutes.
   *
   * @param {number} seconds - Seconds to convert into minutes
   * @param {number} precision - Precision for rounding
   * @return {number} - Minutes
   */
  static secondsToMinutes(seconds, precision = 0) {
    return _round(seconds / 60, precision);
  }

  /**
   * Converts the provided minutes into seconds.
   *
   * @param {number} minutes - Minutes to convert into seconds
   * @param {number} precision - Precision for rounding
   * @return {number} - Seconds
   */
  static minutesToSeconds(minutes) {
    return minutes * 60;
  }

  /**
   * Converts the provided seconds into hours.
   *
   * @param {number} seconds - Seconds to convert into hours
   * @param {number} precision - Precision for rounding
   * @return {number} - Hours
   */
  static secondsToHours(seconds, precision = 0) {
    return _round(seconds / 60 / 60, precision);
  }

  /**
   * Converts the provided hours into seconds.
   *
   * @param {number} hours - Hours to convert into seconds
   * @param {number} precision - Precision for rounding
   * @return {number} - Seconds
   */
  static hoursToSeconds(seconds) {
    return seconds * 60 * 60;
  }

  /**
   * Converts the provided seconds into hours, minutes, and seconds.
   *
   * @param {number} seconds - Seconds to convert
   * @return {object} - Hours, Minutes, Seconds
   */
  static secondsToMinutesHours(seconds) {
    let remainder = seconds;
    const hours = this.secondsToHours(remainder);
    remainder -= this.hoursToSeconds(hours);

    const minutes = this.secondsToMinutes(remainder);
    remainder -= this.minutesToSeconds(minutes);

    return {
      hours,
      minutes,
      seconds: remainder,
    };
  }

  /**
   * Formats time based on the provided seconds and labels. Converts into
   * minutes, hours, and seconds and provides the best fitting label.
   *
   * @param {object} labels - Object with hoursLabel, minutesLabel, and hoursMinutesLabel
   * @param {number} seconds - Seconds to format time for
   * @return {object} - Hours, Minutes, Seconds
   */
  static destructTimeAndLabel({ labels, seconds: initSeconds }) {
    const { hoursLabel, minutesLabel, hoursMinutesLabel } = labels;
    const { hours, minutes, seconds } = this.secondsToMinutesHours(initSeconds);

    let label = hoursMinutesLabel;
    if (hours <= 0) {
      label = minutesLabel;
    } else if (minutes <= 0) {
      label = hoursLabel;
    }

    return {
      hours,
      minutes,
      seconds,
      label,
    };
  }

  /**
   * Rounds the units of time to the nearest provided interval with respect to the
   * provided max and min time values.
   *
   * Example: If we want to round some amount of x minutes to the nearest 5 minutes, with a
   * minimum of 5 minutes, we would call roundToNearest({ time: x, interval: 5, min: 5 })
   *
   * @param {number} time - Units of time
   * @param {number} interval - Interval of time to round to
   * @param {number} min - Min unit of time
   * @param {number} max - Max unit of time
   * @return {number} - Rounded value
   */
  // eslint-disable-next-line object-curly-newline
  static roundToNearest({ time, interval, min = 0, max = Infinity }) {
    const rounded = interval * Math.round(time / interval);
    return Math.max(Math.min(rounded, max), min);
  }
}

export default DateTimeUtils;
